import React, { useMemo, useState } from 'react'
import { Clock, ChefHat, Check, MessageSquare } from 'lucide-react'
import { useProducts } from '../../hooks/useProducts'
import { useTables } from '../../hooks/useTables'
import { useOrders, useOrderStatusMutation } from '../../hooks/useOrders'
import {
  normalizeOrder,
  type NormalizedOrder
} from '../../utils/normalize-order'

type KitchenStatus = 'WAITING' | 'IN_PRODUCTION' | 'DONE'

const STATUS_DETAILS: Record<
  KitchenStatus,
  {
    title: string
    color: string
    icon: React.ElementType
    tabColor: string
  }
> = {
  WAITING: {
    title: 'Fila de espera',
    color: 'border-yellow-500',
    icon: Clock,
    tabColor: 'text-yellow-600 border-yellow-600'
  },
  IN_PRODUCTION: {
    title: 'En producción',
    color: 'border-blue-500',
    icon: ChefHat,
    tabColor: 'text-blue-600 border-blue-600'
  },
  DONE: {
    title: 'Pronto!',
    color: 'border-green-500',
    icon: Check,
    tabColor: 'text-green-600 border-green-600'
  }
}

const nextActionByStatus = {
  WAITING: { label: 'Preparar', next: 'IN_PRODUCTION' },
  IN_PRODUCTION: { label: 'Listo', next: 'DONE' },
  DONE: { label: 'Entregar', next: 'DELIVERED' }
} as const

type DestinationFilter = 'ALL' | 'MESA' | 'TAKEAWAY'

const DESTINATION_OPTIONS: { value: DestinationFilter; label: string }[] = [
  { value: 'ALL', label: 'Todos' },
  { value: 'MESA', label: 'Mesas' },
  { value: 'TAKEAWAY', label: 'Para llevar' }
]

import { echo } from '../../lib/echo'
import { useQueryClient } from '@tanstack/react-query'

const Kitchen: React.FC = () => {
  const queryClient = useQueryClient()
  const [activeStatus, setActiveStatus] = useState<KitchenStatus>('WAITING')
  const [activeDestination, setActiveDestination] =
    useState<DestinationFilter>('ALL')
  const [selectedArea, setSelectedArea] = useState<number>(1)
  const { data: products = [] } = useProducts()
  const { data: tables = [] } = useTables()
  const { data: rawOrders = [], refetch: refetchOrders } = useOrders()
  const statusMutation = useOrderStatusMutation()

  // --- REAL-TIME UPDATES FOR KITCHEN ---
  React.useEffect(() => {
    const tableChannel = echo.channel('pos-updates');
    
    tableChannel.listen('TableUpdated', () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      refetchOrders()
    });

    const orderChannel = echo.channel('orders');
    
    orderChannel.listen('.orders.new', () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      refetchOrders()
    });
    
    orderChannel.listen('.orders.updated', () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      refetchOrders()
    });

    return () => {
      echo.leave('pos-updates');
      echo.leave('orders');
    }
  }, [refetchOrders, queryClient])

  const catalogById = useMemo(() => {
    const map: Record<string, { name: string; price: number }> = {}
    products.forEach(p => {
      map[p.id] = { name: p.name, price: p.price }
    })
    return map
  }, [products])

  const tablesById = useMemo(() => {
    const map: Record<string, string> = {}
    tables.forEach(t => {
      map[t.id] = String(t.number)
    })
    return map
  }, [tables])

  const orders = useMemo(() => {
    return rawOrders
      .map(o => normalizeOrder(o, catalogById, tablesById))
      // KDS muestra órdenes activas de COCINA (WAITING, IN_PRODUCTION, DONE)
      // independientemente del payment_status. Una orden pagada con ítems
      // aún en producción debe seguir visible.
      .filter(o => ['WAITING', 'IN_PRODUCTION', 'DONE'].includes(o.status))
      .sort(
        (a, b) =>
          new Date(b.createdAt ?? 0).getTime() -
          new Date(a.createdAt ?? 0).getTime()
      )
  }, [rawOrders, catalogById, tablesById])

  const filteredByDestination = useMemo(() => {
    if (activeDestination === 'ALL') return orders
    return orders.filter(order => {
      const destination = order.destination ?? 'MESA'
      return destination === activeDestination
    })
  }, [orders, activeDestination])

  const grouped = useMemo(
    () => {
      const filterByArea = (o: NormalizedOrder) => {
        if (!o || !o.items) return null;
        
        const areaItems = o.items.filter(item => {
          if (!item || !item.id) return false;
          const productId = Number(item.id);
          const product = products.find(p => p.id === productId);
          // Si el producto no existe en el catálogo, por defecto va al área 1 para no perderlo
          const area = product?.preparationArea ?? 1;
          return area === selectedArea;
        });
        
        if (areaItems.length === 0) return null;
        
        return { ...o, items: areaItems };
      };

      const areaFilteredOrders = filteredByDestination
        .map(filterByArea)
        .filter((o): o is NormalizedOrder => o !== null)

      return {
        WAITING: areaFilteredOrders.filter(o => o.status === 'WAITING'),
        IN_PRODUCTION: areaFilteredOrders.filter(o => o.status === 'IN_PRODUCTION'),
        DONE: areaFilteredOrders.filter(o => o.status === 'DONE')
      }
    },
    [filteredByDestination, selectedArea, products]
  )

  const counts = {
    WAITING: grouped.WAITING.length,
    IN_PRODUCTION: grouped.IN_PRODUCTION.length,
    DONE: grouped.DONE.length
  }

  const handleAction = (order: NormalizedOrder) => {
    const action = nextActionByStatus[order.status as KitchenStatus]
    if (!action) return
    statusMutation.mutate({ id: order.id, status: action.next })
  }

  return (
    <div className="p-4 md:p-6 text-neutral-900 dark:text-neutral-100">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Cocina</h1>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Pantalla:</label>
          <select 
            value={selectedArea} 
            onChange={(e) => setSelectedArea(Number(e.target.value))}
            className="text-sm border rounded-md p-1 bg-white dark:bg-neutral-800 border-neutral-300 dark:border-neutral-700"
          >
            <option value={1}>Cocina 1</option>
            <option value={2}>Cocina 2</option>
          </select>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {DESTINATION_OPTIONS.map(option => {
          const isActive = option.value === activeDestination
          return (
            <button
              key={option.value}
              onClick={() => setActiveDestination(option.value)}
              className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                isActive
                  ? 'bg-gray-900 text-white border-gray-900 dark:bg-neutral-100 dark:text-neutral-900 dark:border-neutral-100'
                  : 'border-gray-300 text-gray-700 hover:border-gray-500 hover:text-gray-900 dark:border-neutral-700 dark:text-neutral-400 dark:hover:border-neutral-500 dark:hover:text-neutral-200'
              }`}
              aria-pressed={isActive}
            >
              {option.label}
            </button>
          )
        })}
      </div>
      <div className="flex gap-4 mb-4 overflow-x-auto">
        {Object.entries(STATUS_DETAILS).map(
          ([statusKey, { title, icon: Icon, tabColor }]) => {
            const status = statusKey as KitchenStatus
            const isActive = activeStatus === status
            return (
              <button
                key={status}
                onClick={() => setActiveStatus(status)}
                className={`flex items-center gap-2 pb-2 border-b-2 whitespace-nowrap ${
                  isActive
                    ? `${tabColor}`
                    : 'border-transparent text-gray-600 hover:text-gray-800 dark:text-neutral-400 dark:hover:text-neutral-200'
                }`}
                aria-selected={isActive}
              >
                <Icon className="w-5 h-5" /> {title}
                <span className="ml-1 text-xs bg-gray-200 dark:bg-neutral-800 dark:text-neutral-300 rounded-full px-2 py-0.5">
                  {counts[status]}
                </span>
              </button>
            )
          }
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(grouped[activeStatus] ?? []).map((order: NormalizedOrder) => {
          const action = nextActionByStatus[order.status as KitchenStatus]
          return (
            <div
              key={order.id}
              className={`bg-white dark:bg-neutral-800 rounded shadow border-l-4 ${
                STATUS_DETAILS[order.status as KitchenStatus].color
              } p-3 flex flex-col`}
            >
              <span className="text-xl font-bold mb-2 block">
                {order.destination === 'TAKEAWAY' ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                      TAKEAWAY
                    </span>
                    <span>{order.customerName?.trim() || 'Cliente sin nombre'}</span>
                  </span>
                ) : (
                  <>Mesa #{order.tableLabel}</>
                )}{' '}
                ({order.items.length}{' '}
                {order.items.length === 1 ? 'item' : 'items'})
              </span>
              <ul className="text-sm space-y-1 overflow-y-auto max-h-40 pr-1">
                {order.items.map(item => (
                  <li key={item.id} className="leading-snug">
                    <span className="font-semibold">{item.qty}x</span>{' '}
                    <span className="font-medium">{item.name}</span>
                    {(item.fulfillmentType === 'TAKEOUT' || item.fulfillmentType === 'TAKEAWAY') && (
                      <span className="ml-2 inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-800">
                        PARA LLEVAR
                      </span>
                    )}
                    {(item.kitchenSummary || item.additions || item.exceptions) && (
                      <div className="mt-1 text-xs leading-5">
                        {item.kitchenSummary ? (
                          <span>{item.kitchenSummary}</span>
                        ) : (
                          <>
                            {item.additions &&
                              Object.entries(item.additions).map(([k, qty]: [string, any]) =>
                                qty > 0 ? (
                                  <span
                                    key={k}
                                    className="inline-block mr-2 px-2 py-0.5 rounded bg-gray-200 dark:bg-neutral-700 dark:text-neutral-200"
                                  >
                                    + {k} x{qty}
                                  </span>
                                ) : null
                              )}
                            {item.exceptions &&
                              item.exceptions.map(k => (
                                <span
                                  key={k}
                                  className="inline-block mr-2 px-2 py-0.5 rounded bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-100"
                                >
                                  – {k}
                                </span>
                              ))}
                          </>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {order.note && (
                <div className="mt-2 flex items-start text-sm text-gray-700 bg-yellow-100 p-2 rounded">
                  <MessageSquare
                    size={16}
                    className="mr-1 flex-shrink-0 mt-0.5"
                  />
                  <span>{order.note}</span>
                </div>
              )}
              {action && (
                <button
                  onClick={() => handleAction(order)}
                  className="mt-3 self-end text-xs px-2 py-1 bg-gray-800 text-white rounded disabled:opacity-50"
                  disabled={
                    statusMutation.isPending &&
                    statusMutation.variables?.id === order.id
                  }
                  aria-busy={
                    statusMutation.isPending &&
                    statusMutation.variables?.id === order.id
                  }
                >
                  {action.label}
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default Kitchen
